import { useCallback, useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X, Image as ImageIcon, MapPin, Tag, Hash, LockKey, UsersThree,
    Globe, Sparkle, Trash, ArrowLineUp, CaretRight, CaretLeft, UploadSimple,
} from "@phosphor-icons/react";
import { postsApi, uploadsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { WORLD_COUNTRIES } from "@/pages/Dashboard";
import { useTranslation } from "react-i18next";

export default function CreatePost({ open, onClose, onCreated, defaultPrivacy = "public" }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [items, setItems] = useState([]); // {file, previewUrl, kind, id?, url?, uploading, progress}
    const [caption, setCaption] = useState("");
    const [location, setLocation] = useState("");
    const [country, setCountry] = useState("");
    const [destination, setDestination] = useState("");
    const [tagsInput, setTagsInput] = useState("");
    const [hashtagsInput, setHashtagsInput] = useState("");
    const [privacy, setPrivacy] = useState(defaultPrivacy);
    const [cost, setCost] = useState("");
    const [rating, setRating] = useState(0);
    const [tips, setTips] = useState("");
    const [step, setStep] = useState(1); // 1 media, 2 details
    const [dragOver, setDragOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const fileInputRef = useRef(null);

    const PRIVACIES = [
        { k: "public", l: t("createPost.privacy.public.label"), Icon: Globe, d: t("createPost.privacy.public.desc") },
        { k: "followers", l: t("createPost.privacy.followers.label"), Icon: UsersThree, d: t("createPost.privacy.followers.desc") },
        { k: "friends", l: t("createPost.privacy.friends.label"), Icon: UsersThree, d: t("createPost.privacy.friends.desc") },
        { k: "private", l: t("createPost.privacy.private.label"), Icon: LockKey, d: t("createPost.privacy.private.desc") },
    ];
    const MAX_MEDIA = 10;

    // Live City Autocomplete via OpenStreetMap Nominatim
    useEffect(() => {
        if (!location || location.length < 2 || !country) {
            setCitySuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&city=${encodeURIComponent(location)}&country=${encodeURIComponent(country)}&limit=5`);
                if (res.ok) {
                    const data = await res.json();
                    const names = Array.from(new Set(data.map((item) => item.display_name.split(",")[0].trim())));
                    setCitySuggestions(names);
                }
            } catch (err) {
                // ignore nominatim errors silently
            }
        }, 600); // 600ms debounce to stay within Nominatim's 1 req/sec limit
        return () => clearTimeout(timer);
    }, [location, country]);

    const reset = () => {
        // revoke object URLs
        items.forEach((it) => it.previewUrl && it.file && URL.revokeObjectURL(it.previewUrl));
        setItems([]); setCaption(""); setLocation(""); setCountry(""); setDestination("");
        setTagsInput(""); setHashtagsInput(""); setPrivacy(defaultPrivacy); setCost(""); setRating(0);
        setTips(""); setStep(1); setBusy(false); setCitySuggestions([]);
    };

    const handleFiles = useCallback(async (files) => {
        const list = Array.from(files).slice(0, MAX_MEDIA - items.length);
        if (!list.length) return;
        const staged = list.map((file) => ({
            tmp: crypto.randomUUID(),
            file,
            kind: file.type.startsWith("video/") ? "video" : "image",
            previewUrl: URL.createObjectURL(file),
            uploading: true,
            progress: 0,
        }));
        setItems((prev) => [...prev, ...staged]);
        // Upload sequentially to prevent proxy mix-ups with concurrent streams
        for (const it of staged) {
            try {
                const uploaded = await uploadsApi.uploadObject(it.file, (p) => {
                    setItems((prev) => prev.map((x) => x.tmp === it.tmp ? { ...x, progress: p } : x));
                });
                setItems((prev) => prev.map((x) => x.tmp === it.tmp ? { ...x, ...uploaded, uploading: false, progress: 100 } : x));
            } catch (e) {
                toast.error(t("createPost.toasts.uploadFailed", { name: it.file.name }));
                setItems((prev) => prev.filter((x) => x.tmp !== it.tmp));
            }
        }
    }, [items.length, t]);

    const onDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    };

    const removeItem = (tmp) => {
        setItems((prev) => {
            const target = prev.find((x) => x.tmp === tmp);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((x) => x.tmp !== tmp);
        });
    };

    const move = (idx, dir) => {
        setItems((prev) => {
            const next = [...prev];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return prev;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
    };

    const handleNextStep = () => {
        if (!items.length) {
            return toast.error(t("createPost.toasts.mediaRequired", "Please upload at least one image or video to continue."));
        }
        if (items.some((x) => x.uploading)) {
            return toast.error(t("createPost.toasts.waitForUploads", "Please wait for uploads to complete."));
        }
        setStep(2);
    };

    const post = async () => {
        if (!items.length) return toast.error(t("createPost.toasts.mediaRequired", "Please upload at least one image or video."));
        if (!country.trim()) return toast.error(t("createPost.toasts.selectCountry", "Please select a country."));
        if (!location.trim()) return toast.error(t("createPost.toasts.typeCity", "Please enter a city location."));
        if (items.some((x) => x.uploading)) return toast.error(t("createPost.toasts.waitForUploads", "Please wait for uploads to complete."));
        setBusy(true);
        try {
            const media = items.filter((x) => x.id).map((x) => ({
                id: x.id, url: x.url, kind: x.kind, mime: x.mime,
            }));
            const tagged_handles = tagsInput.split(/[,\s]+/).map((s) => s.replace(/^@/, "").trim().toLowerCase()).filter(Boolean);
            const hashtags = hashtagsInput.split(/[,\s]+/).map((s) => s.replace(/^#/, "").trim().toLowerCase()).filter(Boolean);
            const created = await postsApi.create({
                caption: caption.trim(),
                location: location.trim() || null,
                country: country.trim() || null,
                destination: destination.trim() || null,
                media,
                tagged_handles,
                hashtags,
                privacy,
                cost: cost.trim() || null,
                rating: rating || null,
                tips: tips.trim() || null,
            });
            toast.success(t("createPost.toasts.posted"));
            onCreated?.(created);
            reset();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("createPost.toasts.couldNotPost"));
            setBusy(false); // safety reset so the button never stays permanently locked
        } finally { setBusy(false); }
    };

    if (!open) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[90] flex items-center justify-center p-3 md:p-6"
                style={{ background: "rgba(3,7,15,0.9)", backdropFilter: "blur(12px)" }}
                onClick={() => !busy && onClose()}
                data-testid="create-post-modal"
            >
                <motion.div
                    onClick={(e) => e.stopPropagation()}
                    initial={{ y: 16, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }}
                    className="w-full max-w-3xl rounded-3xl overflow-hidden flex flex-col max-h-[92vh]"
                    style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.25)" }}
                >
                    <header className="p-5 md:p-6 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                        <div>
                            <div className="overline mb-1">{t("createPost.overline")}</div>
                            <h3 className="font-serif text-2xl text-white leading-tight">{t("createPost.title")}</h3>
                        </div>
                        <button onClick={onClose} className="w-9 h-9 rounded-full glass grid place-items-center" aria-label="Close" data-testid="create-post-close">
                            <X size={14} />
                        </button>
                    </header>

                    {/* Step indicator */}
                    <div className="flex items-center gap-2 px-6 pt-4">
                        {[1, 2].map((n) => (
                            <div key={n} className="h-1 flex-1 rounded-full" style={{ background: step >= n ? "var(--gold)" : "rgba(255,255,255,0.08)" }} />
                        ))}
                    </div>

                    <div className="p-5 md:p-6 overflow-y-auto flex-1 space-y-5">
                        {step === 1 ? (
                            <>
                                {/* Drop zone */}
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={onDrop}
                                    className="rounded-2xl border-2 border-dashed p-6 md:p-10 text-center transition-colors"
                                    style={{ borderColor: dragOver ? "var(--gold)" : "rgba(255,255,255,0.12)", background: dragOver ? "rgba(212,175,55,0.06)" : "rgba(0,0,0,0.25)" }}
                                    data-testid="create-post-dropzone"
                                >
                                    <UploadSimple size={28} color="#D4AF37" className="mx-auto mb-3" />
                                    <div className="font-serif text-white text-xl mb-1">{t("createPost.dropzone.dragTitle")}</div>
                                    <div className="text-white/50 text-sm mb-4">{t("createPost.dropzone.or")}</div>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="btn-gold text-sm !py-2.5 !px-6"
                                        data-testid="create-post-choose"
                                    >
                                        {t("createPost.dropzone.chooseBtn")}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                                        multiple
                                        hidden
                                        onChange={(e) => e.target.files?.length && handleFiles(e.target.files)}
                                        data-testid="create-post-file-input"
                                    />
                                    <div className="text-white/40 text-xs mt-4 whitespace-pre-line">
                                        {t("createPost.dropzone.info")}
                                    </div>
                                </div>

                                {items.length > 0 && (
                                    <div>
                                        <div className="overline mb-3">{t("createPost.selected", { current: items.length, max: MAX_MEDIA })}</div>
                                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                                            {items.map((it, idx) => (
                                                <div key={it.tmp} className="relative rounded-xl overflow-hidden bg-black aspect-square group" data-testid={`create-post-item-${idx}`}>
                                                    {it.kind === "video" ? (
                                                        <video src={it.previewUrl} className="w-full h-full object-cover" muted playsInline />
                                                    ) : (
                                                        <img src={it.previewUrl} alt="" className="w-full h-full object-cover" />
                                                    )}
                                                    {idx === 0 && (
                                                        <div className="absolute top-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-widest" style={{ background: "var(--gold)", color: "var(--navy)" }}>
                                                            {t("createPost.cover")}
                                                        </div>
                                                    )}
                                                    {it.uploading && (
                                                        <div className="absolute inset-0 grid place-items-center bg-black/50 text-xs text-white">
                                                            {it.progress || 0}%
                                                        </div>
                                                    )}
                                                    <div className="absolute inset-x-1.5 bottom-1.5 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <div className="flex gap-1">
                                                            <button onClick={() => move(idx, -1)} disabled={idx === 0} className="w-7 h-7 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.7)", color: "white" }} aria-label="Move left">
                                                                <CaretLeft size={12} weight="bold" />
                                                            </button>
                                                            <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="w-7 h-7 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.7)", color: "white" }} aria-label="Move right">
                                                                <CaretRight size={12} weight="bold" />
                                                            </button>
                                                        </div>
                                                        <button onClick={() => removeItem(it.tmp)} className="w-7 h-7 rounded-full grid place-items-center" style={{ background: "rgba(220,50,50,0.85)", color: "white" }} aria-label="Remove" data-testid={`remove-media-${idx}`}>
                                                            <Trash size={12} weight="bold" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <textarea
                                    value={caption}
                                    onChange={(e) => setCaption(e.target.value)}
                                    placeholder={t("createPost.placeholders.caption")}
                                    rows={4}
                                    className="w-full bg-black/30 border rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]"
                                    style={{ borderColor: "rgba(255,255,255,0.08)" }}
                                    data-testid="create-post-caption"
                                />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2 bg-black/30 border rounded-xl px-4 py-2.5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                                        <Globe size={14} color="#D4AF37" />
                                        <select 
                                            value={country} 
                                            onChange={(e) => { setCountry(e.target.value); setLocation(""); setCitySuggestions([]); }} 
                                            className="flex-1 bg-transparent text-sm text-white focus:outline-none cursor-pointer appearance-none" 
                                            data-testid="create-post-country"
                                        >
                                            <option value="" disabled>{t("createPost.placeholders.selectCountry")}</option>
                                            {WORLD_COUNTRIES.map((c) => (
                                                <option key={c} value={c} className="bg-slate-900 text-white">{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="relative flex items-center gap-2 bg-black/30 border rounded-xl px-4 py-2.5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                                        <MapPin size={14} color="#D4AF37" />
                                        <input 
                                            disabled={!country}
                                            placeholder={country ? t("createPost.placeholders.typeCity") : t("createPost.placeholders.selectCountryFirst")} 
                                            value={location} 
                                            onChange={(e) => setLocation(e.target.value)} 
                                            className="flex-1 bg-transparent text-sm text-white focus:outline-none" 
                                            data-testid="create-post-location" 
                                        />
                                        {citySuggestions.length > 0 && (
                                            <div className="absolute top-[110%] left-0 right-0 z-20 mt-1 rounded-xl p-1 shadow-2xl max-h-48 overflow-y-auto" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.3)" }}>
                                                {citySuggestions.map((s) => (
                                                    <button key={s} type="button" onClick={() => { setLocation(s); setCitySuggestions([]); }} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors">
                                                        {s}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <Field icon={Sparkle} placeholder={t("createPost.placeholders.destination")} value={destination} onChange={setDestination} testId="create-post-destination" />
                                <Field icon={Tag} placeholder={t("createPost.placeholders.tags")} value={tagsInput} onChange={setTagsInput} testId="create-post-tags" />
                                <Field icon={Hash} placeholder={t("createPost.placeholders.hashtags")} value={hashtagsInput} onChange={setHashtagsInput} testId="create-post-hashtags" />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field icon={Sparkle} placeholder={t("createPost.placeholders.cost")} value={cost} onChange={setCost} testId="create-post-cost" />
                                    <div className="flex items-center gap-2 bg-black/30 border rounded-xl px-4 py-2.5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                                        <span className="text-xs text-white/60 mr-1">{t("createPost.rating")}</span>
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button key={n} onClick={() => setRating(rating === n ? 0 : n)} aria-label={`${n} stars`} data-testid={`create-post-rating-${n}`}>
                                                <Sparkle size={16} weight={n <= rating ? "fill" : "regular"} color={n <= rating ? "#D4AF37" : "rgba(255,255,255,0.4)"} />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <Field icon={Sparkle} placeholder={t("createPost.placeholders.tips")} value={tips} onChange={setTips} testId="create-post-tips" />

                                {/* Privacy */}
                                <div>
                                    <div className="overline mb-2">{t("createPost.privacy.overline")}</div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {PRIVACIES.map(({ k, l, Icon, d }) => (
                                            <button
                                                key={k}
                                                onClick={() => setPrivacy(k)}
                                                className="rounded-xl p-3 text-left transition-colors"
                                                style={{
                                                    background: privacy === k ? "rgba(212,175,55,0.14)" : "rgba(0,0,0,0.25)",
                                                    border: `1px solid ${privacy === k ? "var(--gold)" : "rgba(255,255,255,0.08)"}`,
                                                }}
                                                data-testid={`create-post-privacy-${k}`}
                                            >
                                                <Icon size={14} color={privacy === k ? "#D4AF37" : "white"} />
                                                <div className="mt-1.5 text-sm text-white">{l}</div>
                                                <div className="text-[10px] text-white/50 mt-0.5">{d}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <footer className="p-5 border-t flex items-center justify-end gap-2" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                        <div className="flex gap-2">
                            {step > 1 && (
                                <button onClick={() => setStep(step - 1)} className="btn-ghost text-sm !py-2.5 !px-4" data-testid="create-post-back">{t("createPost.buttons.back")}</button>
                            )}
                            {step < 2 ? (
                                <button onClick={handleNextStep} className="btn-gold text-sm !py-2.5 !px-5" data-testid="create-post-next">
                                    {t("createPost.buttons.next")} <CaretRight size={12} weight="bold" />
                                </button>
                            ) : (
                                <button onClick={post} disabled={busy || items.some((x) => x.uploading)} className="btn-gold text-sm !py-2.5 !px-6" data-testid="create-post-submit">
                                    {busy ? t("createPost.buttons.posting") : t("createPost.buttons.post")}
                                </button>
                            )}
                        </div>
                    </footer>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function Field({ icon: Icon, placeholder, value, onChange, testId }) {
    return (
        <div className="flex items-center gap-2 bg-black/30 border rounded-xl px-4 py-2.5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <Icon size={14} color="#D4AF37" />
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="flex-1 bg-transparent text-sm text-white focus:outline-none"
                data-testid={testId}
            />
        </div>
    );
}
